(() => {
  let phoneLibraryPromise;
  const loadPhoneLibrary = (url) => {
    if (window.libphonenumber) return Promise.resolve(window.libphonenumber);
    if (!phoneLibraryPromise) {
      phoneLibraryPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => resolve(window.libphonenumber);
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
      });
    }
    return phoneLibraryPromise;
  };
  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-responsive-header]').forEach((header) => {
      if (header.dataset.responsiveInitialised) return;
      header.dataset.responsiveInitialised = 'true';
      const inner = header.querySelector('.header__inner');
      const brand = header.querySelector('.header__brand-group');
      const nav = header.querySelector('.header__nav');
      const actions = header.querySelector('.header__actions');
      if (!inner || !brand || !nav || !actions) return;

      let frame;
      const updateHeaderLayout = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          header.classList.remove('header--compact');
          if (window.matchMedia('(max-width: 1100px)').matches) {
            header.classList.add('header--compact');
            return;
          }
          const brandBox = brand.getBoundingClientRect();
          const navBox = nav.getBoundingClientRect();
          const actionsBox = actions.getBoundingClientRect();
          const spacing = 12;
          const collides = brandBox.right + spacing > navBox.left || navBox.right + spacing > actionsBox.left;
          header.classList.toggle('header--compact', inner.scrollWidth > inner.clientWidth + 1 || collides);
        });
      };
      window.addEventListener('resize', updateHeaderLayout, { passive: true });
      document.fonts?.ready.then(updateHeaderLayout);
      updateHeaderLayout();
    });

    scope.querySelectorAll('.mobile-panel').forEach((panel) => {
      if (panel.dataset.initialised) return;
      panel.dataset.initialised = 'true';
      const toggles = document.querySelectorAll('[aria-controls="' + panel.id + '"]');
      const opener = [...toggles].find((node) => !panel.contains(node));
      const setOpen = (open) => {
        panel.hidden = !open;
        toggles.forEach((node) => node.setAttribute('aria-expanded', String(open)));
        document.body.classList.toggle('menu-open', open);
        if (open) panel.querySelector('button,a')?.focus();
        else opener?.focus();
      };
      toggles.forEach((node) => node.addEventListener('click', () => setOpen(panel.hidden)));
      panel.querySelectorAll('a').forEach((node) => node.addEventListener('click', () => setOpen(false)));
      document.addEventListener('keydown', (event) => {
        if (panel.hidden) return;
        if (event.key === 'Escape') setOpen(false);
        if (event.key === 'Tab') {
          const focusable = [...panel.querySelectorAll('a,button,summary')].filter((node) => node.getClientRects().length);
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      });
    });

    scope.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.initialised) return;
      form.dataset.initialised = 'true';
      const select = form.querySelector('[data-variant-select]');
      const price = form.closest('[data-product-root]')?.querySelector('[data-product-price]');
      const submit = form.querySelector('[type="submit"]');
      if (!select) return;
      const update = () => {
        const option = select.options[select.selectedIndex];
        if (!option) return;
        if (price && option.dataset.price) price.textContent = option.dataset.price;
        if (submit) {
          submit.disabled = option.dataset.available !== 'true';
          submit.textContent = option.dataset.available === 'true' ? submit.dataset.addLabel : submit.dataset.soldLabel;
        }
      };
      select.addEventListener('change', update);
      update();
    });

    scope.querySelectorAll('[data-scan-purchase-date]').forEach((date) => {
      if (date.dataset.initialised) return;
      date.dataset.initialised = 'true';
      const today = new Date();
      const min = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      date.min = [min.getFullYear(), String(min.getMonth() + 1).padStart(2, '0'), String(min.getDate()).padStart(2, '0')].join('-');
      const validate = () => {
        if (!date.value) { date.setCustomValidity(''); return; }
        const day = new Date(date.value + 'T12:00:00').getDay();
        date.setCustomValidity(day === 0 || day === 6 ? 'Please choose a Monday-to-Friday date.' : '');
      };
      date.addEventListener('change', validate);
      date.closest('form')?.addEventListener('submit', validate);
    });

    scope.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.initialised) return;
      gallery.dataset.initialised = 'true';
      const main = gallery.querySelector('[data-product-gallery-main]');
      if (!main) return;
      gallery.querySelectorAll('[data-product-gallery-thumb]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!button.dataset.fullSource) return;
          main.removeAttribute('srcset');
          main.src = button.dataset.fullSource;
          main.alt = button.dataset.fullAlt || '';
          gallery.querySelectorAll('[data-product-gallery-thumb]').forEach((node) => node.setAttribute('aria-current', String(node === button)));
        });
      });
    });

    scope.querySelectorAll('[data-assessment-form]').forEach((form) => {
      if (form.dataset.initialised) return;
      form.dataset.initialised = 'true';
      const name = form.querySelector('[data-full-name]');
      const country = form.querySelector('[data-country-code]');
      const local = form.querySelector('[data-local-phone]');
      const complete = form.querySelector('[data-complete-phone]');
      const error = form.querySelector('[data-phone-error]');
      name?.addEventListener('input', () => {
        name.setCustomValidity(name.value.trim().length < 2 ? name.dataset.invalidMessage : '');
      });
      if (!country || !local || !complete) return;
      // Country names follow the storefront language without a geolocation request.
      try {
        const names = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' });
        [...country.options].forEach((option) => { option.textContent = names.of(option.value) + ' (' + option.dataset.dial + ')'; });
      } catch (_) { /* Keep the accessible English fallback options. */ }
      const updatePhone = () => {
        const raw = local.value.trim();
        let valid = !raw, normalised = '';
        if (raw && window.libphonenumber) {
          const parsed = window.libphonenumber.parsePhoneNumberFromString(raw, country.value);
          valid = Boolean(parsed && parsed.isPossible());
          if (valid) normalised = parsed.number;
        } else if (raw) {
          const digits = raw.replace(/\D/g, '');
          valid = /^[+0-9\s().-]+$/.test(raw) && digits.length >= 6 && digits.length <= 15;
          // On a blocked script, preserve the original number with its selected country.
          if (valid) normalised = country.options[country.selectedIndex].dataset.dial + ' ' + raw;
        }
        complete.value = normalised;
        local.setCustomValidity(valid ? '' : error.textContent.trim());
        local.setAttribute('aria-invalid', String(!valid));
        error.hidden = valid;
        return valid;
      };
      const warmPhone = () => loadPhoneLibrary(local.dataset.validationSource).then(updatePhone);
      local.addEventListener('focus', warmPhone, { once: true });
      local.addEventListener('input', updatePhone);
      country.addEventListener('change', updatePhone);
      form.addEventListener('submit', async (event) => {
        if (name) name.value = name.value.trim();
        if (local.value.trim() && !window.libphonenumber && !form.dataset.phoneChecked) {
          event.preventDefault();
          await warmPhone();
          form.dataset.phoneChecked = 'true';
          if (updatePhone() && form.reportValidity()) form.requestSubmit(event.submitter || undefined);
          return;
        }
        if (!updatePhone()) { event.preventDefault(); local.reportValidity(); }
      });
    });

    scope.querySelectorAll('[data-booking]').forEach((root) => {
      if (root.dataset.initialised) return;
      root.dataset.initialised = 'true';
      const centre = root.querySelector('[data-centre]');
      const date = root.querySelector('[data-date]');
      const slots = root.querySelector('.slots[data-slots]');
      const slotInput = root.querySelector('[data-slot-input]');
      if (!centre || !date || !slots || !slotInput) return;
      const today = new Date();
      const min = new Date(today.getFullYear(), today.getMonth(), today.getDate() + Number(root.dataset.notice || 2));
      const max = new Date(today.getFullYear(), today.getMonth(), today.getDate() + Number(root.dataset.window || 60));
      const iso = (day) => [day.getFullYear(), String(day.getMonth() + 1).padStart(2, '0'), String(day.getDate()).padStart(2, '0')].join('-');
      date.min = iso(min); date.max = iso(max);
      const renderSlots = () => {
        slots.replaceChildren(); slotInput.value = '';
        const option = centre.options[centre.selectedIndex];
        const message = document.createElement('p');
        message.className = 'form-hint';
        if (!option?.value || !date.value) {
          message.textContent = root.dataset.emptyMessage || '';
          slots.appendChild(message); return;
        }
        const day = new Date(date.value + 'T12:00:00').getDay();
        const days = (option.dataset.days || '1,2,3,4,5').split(',').map(Number);
        const blocked = (option.dataset.blocked || '').split(',').map((value) => value.trim()).filter(Boolean);
        if (!days.includes(day) || blocked.includes(date.value) || !date.checkValidity()) {
          message.className = 'form-error';
          message.textContent = root.dataset.closedMessage || '';
          slots.appendChild(message); return;
        }
        (option.dataset.slots || '09:00,10:30,12:00,14:00,15:30').split(',').map((value) => value.trim()).filter(Boolean).forEach((time) => {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'slot-button'; button.textContent = time;
          button.setAttribute('aria-pressed', 'false');
          button.addEventListener('click', () => {
            slots.querySelectorAll('button').forEach((item) => {
              item.classList.toggle('is-selected', item === button);
              item.setAttribute('aria-pressed', String(item === button));
            });
            slotInput.value = time;
            const error = root.querySelector('[data-booking-error]');
            if (error) error.textContent = '';
          });
          slots.appendChild(button);
        });
      };
      centre.addEventListener('change', renderSlots);
      date.addEventListener('change', renderSlots);
      root.closest('form')?.addEventListener('submit', (event) => {
        if (!centre.value || !date.value || !slotInput.value || !date.checkValidity()) {
          event.preventDefault();
          const error = root.querySelector('[data-booking-error]');
          if (error) error.textContent = root.dataset.requiredMessage || '';
        }
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
